import { createStore, StoreCore } from "@priolo/jon";
import attachApi from "api/attachments";
import incidentsApi from "api/incidents";
import i18n from "i18next";
import cycleDialogSo from "stores/cycle/editDialog";
import farmSo from "stores/farm";
import growUnitSo from "stores/growUnit";
import dialogSo, { DIALOG_TYPE } from "stores/layout/dialogStore";
import { LastDateRange } from "stores/planner/utils/fetch";
import { DateRange } from "types/global";
import { Incident, IncidentType } from "types/Incident";
import { haveValueDateRange, normalizeDateRanges } from "utils";



const setup = {

	state: {
		/** tutti gli INCIDENTS */
		all: <Incident[]>[],

		/** 
		 * INCIDENT selezionato in questo momento 
		 * Usato per l'editazione
		 * */
		select: <Incident>null,
		/** tutti i tipi di INCIDENTS */
		types: <IncidentType[]>[],

		/** l'id FARM dell'ultima "caricata" di INCIDENTS*/
		lastFarmId: <number>null,

		/** indica che la dialog per l'editazione dell INCIDENT è aperto */
		editDialogOpen: <boolean>false,
	},

	getters: {
		getByCycleId: (cycleId: number, store?: IncidentStore) => {
			return store.state.all.filter(i => i.cycleId == cycleId)
		},
		getLabelType: (typeId: string, store?: IncidentStore) => {
			const type = store.state.types.find(t => t.id == typeId)
			return type?.label ?? "--"
		},
		getById: (id: number, store?: IncidentStore) => {
			return store.state.all?.find(i => i.id == id)
		}
	},

	actions: {

		/** 
		 * Carico tutti i gli INCIDENT di una specifica FARM per uno specifico intervallo di tempo 
		 * ATTUALMENTE NON USATO
		 * */
		fetch: async (
			payload: { farmId?: number, dateRange?: DateRange, force?: boolean, add?: boolean },
			store?: IncidentStore
		) => {
			// controllo ci siano tutti i dati
			const farmId = payload?.farmId ?? growUnitSo.state.lastFarmId
			const dateRange = normalizeDateRanges([payload?.dateRange, LastDateRange])
			if (farmId == null || !haveValueDateRange(dateRange)) return

			// a sto punto carico di brutto...
			let items = []
			try {
				items = (await incidentsApi.index(farmId, null, dateRange))?.data ?? []
			} catch (e) {
				console.error("Error fetching alerts", e)
			}
			store.state.lastFarmId = farmId

			// se devo aggiungere...
			if (payload.add) {
				store.setAll([...store.state.all, ...items])
				// ...altrimenti sostituisco tutto
			} else {
				store.setAll(items)
			}
		},
		fetchIfVoid: async (farmId: number, store?: IncidentStore) => {
			if (store.state.all != null && farmId == store.state.lastFarmId) return
			store.fetch({
				farmId,
				dateRange: LastDateRange,
			})
		},

		/**
		 * Recupero tutti gli INCIDENTS rispetto ad un CYCLE
		 */
		fetchFromCycleId: async (cycleId: number, store?: IncidentStore) => {
			const items = (await incidentsApi.indexFromCycleId(cycleId))?.data ?? []
			store.setAll(items)
		},

		/**
		 * Carico un INCIDENT specifico
		 */
		fetchAndSelect: async (id: number, store?: IncidentStore) => {
			const { data } = await incidentsApi.get(id)
			store.setSelect(data)
		},
		/**
		 * Recupero tutti i tipi di INCIDENTS
		 */
		fetchTypes: async (_: void, store?: IncidentStore) => {
			const { data } = await incidentsApi.types()
			store.setTypes(data)
		},
		fetchTypesIfVoid: async (_: void, store?: IncidentStore) => {
			if (store.state.types?.length > 0) return
			await store.fetchTypes()
		},


		/**
		 * Seleziono e metto in modifica un INCIDENT
		 */
		edit: (incident: Incident, store?: IncidentStore) => {
			if (!incident) return
			// clono gli attachments per evitare modifiche dirette allo store
			store.setSelect({
				...incident,
				attachments: incident.attachments?.map(a => ({ ...a })) || [],
			})
			store.setEditDialogOpen(true)
		},
		/**
		 * Salvo un INCIDENT (nuovo o modificato)
		 */
		save: async (attachFarmId?: number, store?: IncidentStore) => {
			const incidentSel = store.state.select
			const isNew = !incidentSel.id

			// salvo l'incident
			const { data: incidentSaved } = await incidentsApi.save(incidentSel)

			// prelevo il CYCLE per i riferimenti
			const cycleId = cycleDialogSo.state.cycle.id // incidentSaved.cycleId
			const growUnitId = cycleDialogSo.state.cycle.growUnitId // cycleSo.getById(incidentSaved.cycleId)?.growUnitId

			// salvo gli attachments collegati
			const cropLotId = incidentSaved.lostTraysPerCropLot?.[0]?.cropLotId
			attachFarmId = attachFarmId ?? farmSo.state.select?.id
			const newAttachments = store.state.select.attachments || []
			const oldAttachments = store.getById(store.state.select.id)?.attachments || []
			if (!!attachFarmId && !(isNew && newAttachments.length == 0) ) {
				await attachApi.manageDiff(
					attachFarmId, 
					incidentSaved.id, "Incident",
					oldAttachments, newAttachments,
					growUnitId, cropLotId, cycleId
				)
			}

			// ricarico l'incident e aggiorno lo store
			const { data: incident } = await incidentsApi.get(incidentSaved.id)
			if (incident.cycleId == null) incident.cycleId = incidentSaved.cycleId
			const index = store.state.all.findIndex(i => i.id == incident.id)
			if (index == -1) store.state.all.push(incident)
			else store.state.all[index] = incident
			store.setAll([...store.state.all])

			store.setEditDialogOpen(false)
			store.setSelect(null)
			dialogSo.dialogOpen({ type: DIALOG_TYPE.INFO, modal: false, text: i18n.t("snackbar.default.save") })
		},
		/**
		 * modifica l'ICIDENT come REVERTED
		 */
		revert: async (incident: Incident, store?: IncidentStore) => {
			const { data } = await incidentsApi.revert(incident.id)
			incident.reverted = true
			const index = store.state.all.findIndex(i => i.id == incident.id)
			store.state.all[index] = incident
			store.setSelect(data)
		},
		/**
		 * modifica l'ICIDENT come PROCEEDED
		 */
		proceeded: async (incident: Incident, store?: IncidentStore) => {
			const { data } = await incidentsApi.proceeded(incident.id)
			incident.reverted = false
			const index = store.state.all.findIndex(i => i.id == incident.id)
			store.state.all[index] = incident
			store.setSelect(data)
		},
		/**
		 * Elimina un INCIDENT
		 */
		delete: async (incident: Incident, store?: IncidentStore) => {
			await incidentsApi.destroy(incident.id)
			store.setAll(store.state.all.filter(i => i.id != incident.id))
			store.setSelect(null)
		},
	},

	mutators: {
		setAll: (all: Incident[]) => ({ all }),
		setSelect: (select: Incident) => ({ select }),
		setEditDialogOpen: (editDialogOpen: boolean) => ({ editDialogOpen }),
		setTypes: (types: IncidentType[]) => ({ types }),
	},
}


export type IncidentState = typeof setup.state
export type IncidentGetters = typeof setup.getters
export type IncidentActions = typeof setup.actions
export type IncidentMutators = typeof setup.mutators
export interface IncidentStore extends StoreCore<IncidentState>, IncidentGetters, IncidentActions, IncidentMutators {
	state: IncidentState
}
const incidentSo = createStore(setup) as IncidentStore
export default incidentSo